import { UseMutationOptions, useQueryClient } from '@tanstack/react-query';

import useUpdateAddress from '@/api/mutations/addresses/useUpdateAddress';
import { mergeOptimisticAddressUpdate } from '@/api/mutations/addresses/mergeOptimisticAddressUpdate';
import customerKeys from '@/api/queries/customers/keyFactory';
import {
  CustomerQuery,
  UpdateAddressInput,
  UpdateAddressMutation,
} from '@/gql/graphql';

const useOptimisticUpdateCustomerAddress = (
  customerId: string,
  options?: UseMutationOptions<
    UpdateAddressMutation,
    unknown,
    UpdateAddressInput
  >
) => {
  const queryClient = useQueryClient();

  return useUpdateAddress({
    ...options,
    onMutate: async (variables) => {
      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({
        queryKey: customerKeys.customer(customerId),
      });

      // Snapshot the previous value
      const previousCustomer = queryClient.getQueryData(
        customerKeys.customer(customerId)
      );

      // Optimistically update to the new value
      queryClient.setQueryData<CustomerQuery>(
        customerKeys.customer(customerId),
        (old) => {
          if (!old) return;

          return {
            ...old,
            customer: {
              ...old.customer,
              addresses: old.customer.addresses.map((i) =>
                i.id === variables.id
                  ? mergeOptimisticAddressUpdate(i, variables)
                  : i
              ),
            },
          };
        }
      );

      options?.onMutate?.(variables);

      // Return a context object with the snapshotted value
      return { previousCustomer };
    },
    onError: (err, newTodo, context) => {
      // If an error happens, rollback!
      queryClient.setQueryData(
        customerKeys.customer(customerId),
        // @ts-expect-error
        context.previousCustomer
      );

      options?.onError?.(err, newTodo, context);
    },
    // Always refetch after error or success:
    onSettled: (...args) => {
      // oxlint-disable-next-line typescript/no-floating-promises -- FIXME: pre-existing error: Promises must be awaited.
      queryClient.invalidateQueries({
        queryKey: customerKeys.customer(customerId),
      });

      options?.onSettled?.(...args);
    },
  });
};

export default useOptimisticUpdateCustomerAddress;
